import { call, put, take, takeLatest } from "redux-saga/effects";
import { push } from "connected-react-router";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import * as types from "src/state/ducks/notificationTemplate/types";
import * as actions from "src/state/ducks/notificationTemplate/actions";
import * as api from "src/state/ducks/notificationTemplate/api";
import { Status } from "src/constants/Interface";
import { toaster } from "src/helpers/UtilityHelper";
import ValidationManager from "src/firebase/db/ValidationManager";
import {
  NotificationTemplateEntity,
  notificationTemplateEntityKeys,
  NotificationTemplateBakedBreadEntity,
} from "src/firebase/db/NotificationTemplates";
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

function* validateNotificationTemplate() {
  while (true) {
    const { payload } = yield take(types.VALIDATE_NOTIFICATION_TEMPLATE);

    // 入力された値を検証する
    const form = payload as NotificationTemplateEntity;
    const validator = new NotificationTemplateValidationManager();

    // 入力必須
    const IS_REQUIRED = [
      "name",
      "scheduled_time",
      "last_pickup_time",
      "can_order_time",
    ];

    notificationTemplateEntityKeys.forEach((key) => {
      if (IS_REQUIRED.includes(key)) {
        validator.isRequired(key, form[key]);
      }

      const times = {
        scheduledTime: form["scheduled_time"],
        canOrderTime: form["can_order_time"],
        lastPickupTime: form["last_pickup_time"],
      };

      switch (key) {
        case "can_order_time": {
          validator.isValidCanOrderTime(times);
          break;
        }
        case "last_pickup_time": {
          validator.isValidLastPickupTime(times);
          break;
        }
        case "baked_breads": {
          validator.isValidBakedBreads(form["baked_breads"]);
          break;
        }
      }
    });

    if (validator.isPass()) {
      console.log("isPass!!!");
      console.log(payload);
      yield put(actions.successValidateNotificationTemplate(payload));
    } else {
      console.log("hasError!");
      console.log(validator.getErrors());
      const errors = validator.throwValidateError(validator.getErrors());
      yield put(actions.failValidateNotificationTemplate(errors));
    }
  }
}

function* getNotificationTemplate() {
  while (true) {
    const { payload } = yield take(types.GET_NOTIFICATION_TEMPLATE);
    try {
      const res = yield call(api.getNotificationTemplate, payload);
      if (res.status === Status.SUCCESS) {
        yield put(actions.successGetNotificationTemplate(res));
      } else if (res.status === Status.INVALID) {
        yield put(actions.failGetNotificationTemplate(res));
      }
    } catch (err) {
      yield put(
        actions.failGetNotificationTemplate({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        })
      );
    }
  }
}

function* getNotificationTemplates() {
  while (true) {
    const { payload } = yield take(types.GET_NOTIFICATION_TEMPLATES);
    try {
      const res = yield call(api.getNotificationTemplates, payload);
      if (res.status === Status.SUCCESS) {
        yield put(actions.successGetNotificationTemplates(res));
      } else if (res.status === Status.INVALID) {
        yield put(actions.failGetNotificationTemplates(res));
      }
    } catch (err) {
      console.log(err);
      yield put(
        actions.failGetNotificationTemplates({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        })
      );
    }
  }
}

function* createNotificationTemplate() {
  while (true) {
    const { payload } = yield take(types.CREATE_NOTIFICATION_TEMPLATE);
    try {
      const res = yield call(api.createNotificationTemplate, payload);
      if (res.status === Status.SUCCESS) {
        yield put(actions.successCreateNotificationTemplate(res));
        yield put(push("/notification_templates"));
        toaster("success", "テンプレートを作成しました。");
      } else if (res.status === Status.INVALID) {
        yield put(actions.failCreateNotificationTemplate(res));
      }
    } catch (err) {
      yield put(
        actions.failCreateNotificationTemplate({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        })
      );
    }
  }
}

function* updateNotificationTemplate() {
  while (true) {
    const { payload } = yield take(types.UPDATE_NOTIFICATION_TEMPLATE);
    try {
      const res = yield call(api.updateNotificationTemplate, payload);
      if (res.status === Status.SUCCESS) {
        yield put(actions.successUpdateNotificationTemplate(res));
        yield put(push("/notification_templates"));
        toaster("success", "テンプレートを更新しました。");
      } else if (res.status === Status.INVALID) {
        yield put(actions.failUpdateNotificationTemplate(res));
      }
    } catch (err) {
      yield put(
        actions.failUpdateNotificationTemplate({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.me1ssage,
          },
        })
      );
    }
  }
}

function* deleteNotificationTemplate() {
  while (true) {
    const { payload } = yield take(types.DELETE_NOTIFICATION_TEMPLATE);
    try {
      const res = yield call(api.deleteNotificationTemplate, payload);
      const { status, data: notificationTemplate } = res;

      if (status === Status.SUCCESS) {
        yield put(actions.successDeleteNotificationTemplate(res));
        yield put(
          actions.getNotificationTemplates({
            shopId: notificationTemplate.shop_id,
          })
        );
      } else if (status === Status.INVALID) {
        yield put(actions.failDeleteNotificationTemplate(res));
      }
    } catch (err) {
      console.log(err);
      yield put(
        actions.failDeleteNotificationTemplate({
          status: Status.FAILURE,
          error: {
            code: 500,
            message: err.message,
          },
        })
      );
    }
  }
}

function* actionWatcher() {
  yield takeLatest("SUCCESS_CREATE_NOTIFICATION", getNotificationTemplates);
  yield takeLatest(
    "SUCCESS_CREATE_NOTIFICATION_TEMPLATE",
    getNotificationTemplates
  );
  yield takeLatest(
    "SUCCESS_UPDATE_NOTIFICATION_TEMPLATE",
    getNotificationTemplates
  );
  yield takeLatest(
    "SUCCESS_DELETE_NOTIFICATION_TEMPLATE",
    getNotificationTemplates
  );
}

const template = [
  getNotificationTemplate(),
  getNotificationTemplates(),
  validateNotificationTemplate(),
  createNotificationTemplate(),
  updateNotificationTemplate(),
  deleteNotificationTemplate(),
  actionWatcher(),
];

class NotificationTemplateValidationManager extends ValidationManager {
  isValidBakedBreads(bakedBreads: NotificationTemplateBakedBreadEntity[]) {
    const key = "baked_breads";
    const selectedBakedBreads = bakedBreads.filter(
      (bakedBread) => bakedBread.selected
    );

    if (selectedBakedBreads.length === 0) {
      this.addErrors(key, "パンを少なくとも1つ以上選択してください。");
      return;
    }

    const validation = this;
    selectedBakedBreads.forEach((bakedBread) => {
      const { ref, stock, maximum_order } = bakedBread;
      const target = `${key}|${ref.id}`;
      if (stock < 1) {
        validation.addErrors(
          target,
          "販売個数を少なくとも1つ以上設定してください。"
        );
      }
      if (maximum_order < 1) {
        validation.addErrors(
          target,
          "購入上限数を少なくとも1つ以上設定してください。"
        );
      }
    });
  }

  isValidCanOrderTime(params: {
    scheduledTime: Date | undefined;
    canOrderTime: Date | undefined;
    lastPickupTime: Date | undefined;
  }) {
    const { scheduledTime, canOrderTime, lastPickupTime } = params;

    if (!canOrderTime) return;

    const bakedAt = dayjs(scheduledTime);
    const canOrderBy = dayjs(canOrderTime);
    const lastPickupAt = dayjs(lastPickupTime);
    const key = "can_order_time";
    if (scheduledTime && canOrderBy.isSameOrBefore(bakedAt)) {
      this.addErrors(
        key,
        "ラストオーダー時刻が、焼き上がりの時刻以前になっています。"
      );
    } else if (lastPickupTime && canOrderBy.isSameOrAfter(lastPickupAt)) {
      this.addErrors(
        key,
        "ラストオーダー時刻が、パンを受け取れる最終時刻以後になっています。"
      );
    }
  }

  isValidLastPickupTime(params: {
    scheduledTime: Date | undefined;
    canOrderTime: Date | undefined;
    lastPickupTime: Date | undefined;
  }) {
    const { scheduledTime, canOrderTime, lastPickupTime } = params;

    if (!lastPickupTime || !scheduledTime || !canOrderTime) return;

    const bakedAt = dayjs(scheduledTime);
    const canOrderBy = dayjs(canOrderTime);
    const lastPickupAt = dayjs(lastPickupTime);
    const key = "last_pickup_time";
    if (lastPickupAt.isSameOrBefore(bakedAt)) {
      this.addErrors(
        key,
        "パンを受け取れる最終時刻が、焼き上がりの時刻以前になっています。"
      );
    } else if (lastPickupAt.isSameOrBefore(canOrderBy)) {
      this.addErrors(
        key,
        "パンを受け取れる最終時刻が、ラストオーダー時刻以前になっています。"
      );
    }
  }
}

export default template;
